import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { accountApi } from '../../api/account';
import type { CreateReturnRequestDto, ReturnReason } from '../../api/types';
import { returnReasonValues } from '../../api/types';
import {
  Badge,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Pagination,
  Spinner,
} from '../../components/ui';
import { useToast } from '../../components/Toast';
import { formatDate, formatDateTime, money } from '../../lib/format';
import {
  financialStatusLabels,
  fulfillmentStatusLabels,
  labelOf,
  orderStatusLabels,
  returnReasonLabels,
  returnStatusLabels,
  shipmentStatusLabels,
} from '../../lib/labels';

/* --------------------------- Siparis listesi --------------------------- */

export function MyOrdersPage() {
  const [page, setPage] = useState(1);

  const orders = useQuery({
    queryKey: ['account', 'orders', page],
    queryFn: () => accountApi.myOrders({ page, pageSize: 10 }),
    placeholderData: keepPreviousData,
  });

  if (orders.isLoading) return <PageLoader />;
  if (orders.isError) return <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />;

  const data = orders.data!;

  if (data.items.length === 0) {
    return (
      <EmptyState
        title="Henüz siparişiniz yok"
        description="İlk siparişinizi verdiğinizde burada listelenecek."
        action={
          <Link to="/urunler" className="btn-primary">
            Alışverişe başla
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <ul className="space-y-4">
        {data.items.map((order) => (
          <li key={order.publicId} className="card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Link
                  to={`/hesabim/siparisler/${order.publicId}`}
                  className="text-base font-semibold text-ink-900 hover:text-brand-700"
                >
                  {order.orderNumber}
                </Link>
                <p className="mt-0.5 text-sm text-ink-500">
                  {formatDate(order.createdAt)} · {order.itemCount} ürün
                </p>
              </div>

              <div className="text-right">
                <p className="text-base font-semibold text-ink-900">
                  {money(order.grandTotal, order.currencyCode)}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Badge label={labelOf(orderStatusLabels, order.status)} />
              <Badge label={labelOf(financialStatusLabels, order.financialStatus)} />
              <Badge label={labelOf(fulfillmentStatusLabels, order.fulfillmentStatus)} />
            </div>

            <div className="mt-4">
              <Link to={`/hesabim/siparisler/${order.publicId}`} className="btn-secondary btn-sm">
                Detayları gör
              </Link>
            </div>
          </li>
        ))}
      </ul>

      <Pagination
        page={data.page}
        totalPages={data.totalPages}
        totalCount={data.totalCount}
        onChange={setPage}
      />
    </div>
  );
}

/* --------------------------- Siparis detayi --------------------------- */

export function MyOrderDetailPage() {
  const { publicId = '' } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);

  const order = useQuery({
    queryKey: ['account', 'order', publicId],
    queryFn: () => accountApi.myOrder(publicId),
    enabled: publicId.length > 0,
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => accountApi.cancelMyOrder(publicId, reason),
    onSuccess: (message) => {
      toast.success(message ?? 'Siparişiniz iptal edildi.');
      setCancelOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['account'] });
    },
    onError: (error) => toast.fromError(error, 'Sipariş iptal edilemedi.'),
  });

  if (order.isLoading) return <PageLoader />;
  if (order.isError) return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;

  const data = order.data!;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/hesabim/siparisler" className="text-sm text-ink-500 hover:text-ink-800">
            ← Siparişlerim
          </Link>
          <h2 className="mt-1 text-xl font-semibold text-ink-900">{data.orderNumber}</h2>
          <p className="text-sm text-ink-500">{formatDateTime(data.createdAt)}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {data.canCancel ? (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setCancelOpen(true)}>
              Siparişi iptal et
            </button>
          ) : null}
          {data.canReturn ? (
            <button type="button" className="btn-primary btn-sm" onClick={() => setReturnOpen(true)}>
              İade talebi oluştur
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge label={labelOf(orderStatusLabels, data.status)} />
        <Badge label={labelOf(financialStatusLabels, data.financialStatus)} />
        <Badge label={labelOf(fulfillmentStatusLabels, data.fulfillmentStatus)} />
      </div>

      {/* Kalemler */}
      <section className="card overflow-hidden">
        <h3 className="border-b border-ink-200 px-5 py-3.5 text-sm font-semibold text-ink-900">
          Ürünler
        </h3>
        <ul className="divide-y divide-ink-100">
          {data.lines.map((line) => (
            <li key={line.id} className="flex gap-4 p-5">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-ink-50">
                {line.imageUrl ? (
                  <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink-900">
                  {line.productSlug ? (
                    <Link to={`/urun/${line.productSlug}`} className="hover:text-brand-700">
                      {line.productTitle}
                    </Link>
                  ) : (
                    line.productTitle
                  )}
                </p>
                {line.variantTitle ? (
                  <p className="text-xs text-ink-500">{line.variantTitle}</p>
                ) : null}
                <p className="mt-0.5 text-xs text-ink-400">{line.sku}</p>
                <p className="mt-1 text-sm text-ink-600">
                  {line.quantity} × {money(line.unitPrice, data.currencyCode)}
                </p>
                {line.refundedQuantity > 0 ? (
                  <p className="mt-1 text-xs text-amber-700">
                    {line.refundedQuantity} adet iade edildi
                  </p>
                ) : null}
                {line.downloadUrl ? (
                  <a href={line.downloadUrl} className="link mt-1 inline-block text-xs">
                    Dijital ürünü indir
                  </a>
                ) : null}
                {line.productSlug && (data.fulfillmentStatus === 'Fulfilled' || data.status === 'Archived' || (data.fulfillments && data.fulfillments.some((f) => f.state === 'Delivered'))) ? (
                  <div className="mt-2">
                    <Link
                      to={`/urun/${line.productSlug}#degerlendirme`}
                      className="inline-flex items-center gap-1 rounded bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 transition hover:bg-brand-100"
                    >
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                      Ürünü Değerlendir
                    </Link>
                  </div>
                ) : null}
              </div>
              <p className="text-sm font-semibold text-ink-900">
                {money(line.lineTotal, data.currencyCode)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tutarlar */}
        <section className="card p-5">
          <h3 className="mb-4 text-sm font-semibold text-ink-900">Ödeme özeti</h3>
          <dl className="space-y-2 text-sm">
            <Line label="Ara toplam" value={money(data.subTotal, data.currencyCode)} />
            {data.discountTotal > 0 ? (
              <Line label="İndirim" value={`− ${money(data.discountTotal, data.currencyCode)}`} />
            ) : null}
            <Line label="Kargo" value={money(data.shippingTotal, data.currencyCode)} />
            {data.taxLines.map((tax) => (
              <Line
                key={tax.title}
                label={`${tax.title} (%${tax.rate})`}
                value={money(tax.amount, data.currencyCode)}
              />
            ))}
            {data.giftCardTotal > 0 ? (
              <Line label="Hediye kartı" value={`− ${money(data.giftCardTotal, data.currencyCode)}`} />
            ) : null}
            <div className="flex justify-between border-t border-ink-200 pt-2 text-base font-semibold text-ink-900">
              <dt>Toplam</dt>
              <dd>{money(data.grandTotal, data.currencyCode)}</dd>
            </div>
            <Line label="Ödenen" value={money(data.paidTotal, data.currencyCode)} />
            {data.refundedTotal > 0 ? (
              <Line label="İade edilen" value={money(data.refundedTotal, data.currencyCode)} />
            ) : null}
            {data.outstandingTotal > 0 ? (
              <Line label="Kalan" value={money(data.outstandingTotal, data.currencyCode)} />
            ) : null}
          </dl>

          {data.invoiceUrl ? (
            <a href={data.invoiceUrl} className="btn-secondary btn-sm mt-4" target="_blank" rel="noreferrer">
              Faturayı görüntüle
            </a>
          ) : null}
        </section>

        {/* Adres */}
        <section className="card p-5">
          <h3 className="mb-4 text-sm font-semibold text-ink-900">Teslimat</h3>
          {data.shippingAddress ? (
            <address className="text-sm not-italic leading-6 text-ink-600">
              <span className="font-medium text-ink-900">
                {data.shippingAddress.firstName} {data.shippingAddress.lastName}
              </span>
              <br />
              {data.shippingAddress.addressLine1}
              {data.shippingAddress.addressLine2 ? (
                <>
                  <br />
                  {data.shippingAddress.addressLine2}
                </>
              ) : null}
              <br />
              {data.shippingAddress.district} / {data.shippingAddress.city}
              {data.shippingAddress.postalCode ? ` ${data.shippingAddress.postalCode}` : ''}
              <br />
              {data.shippingAddress.countryName}
              <br />
              {data.shippingAddress.phone}
            </address>
          ) : (
            <p className="text-sm text-ink-500">Bu sipariş için teslimat adresi yok.</p>
          )}

          {data.shippingMethodName ? (
            <p className="mt-3 text-sm text-ink-600">Kargo: {data.shippingMethodName}</p>
          ) : null}

          {data.estimatedDeliveryFrom ? (
            <p className="mt-1 text-sm text-ink-600">
              Tahmini teslim: {formatDate(data.estimatedDeliveryFrom)}
              {data.estimatedDeliveryTo ? ` – ${formatDate(data.estimatedDeliveryTo)}` : ''}
            </p>
          ) : null}

          {data.customerNote ? (
            <p className="mt-3 rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-600">
              Notunuz: {data.customerNote}
            </p>
          ) : null}
        </section>
      </div>

      {/* Sevkiyatlar */}
      {data.fulfillments.length > 0 ? (
        <section className="card p-5">
          <h3 className="mb-4 text-sm font-semibold text-ink-900">Kargo takibi</h3>
          <ul className="space-y-4">
            {data.fulfillments.map((fulfillment) => (
              <li key={fulfillment.id} className="rounded-lg border border-ink-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-ink-900">
                      {fulfillment.carrierName ?? 'Kargo'} · {fulfillment.fulfillmentNumber}
                    </p>
                    {fulfillment.trackingNumber ? (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="font-mono text-xs font-medium text-ink-700 bg-ink-100 px-2 py-0.5 rounded">
                          Takip No: {fulfillment.trackingNumber}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (navigator.clipboard) {
                              void navigator.clipboard.writeText(fulfillment.trackingNumber!);
                              toast.success('Kargo takip numarası kopyalandı.');
                            }
                          }}
                          className="text-[11px] text-brand-600 hover:text-brand-800 font-medium"
                        >
                          Kopyala
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <Badge label={labelOf(shipmentStatusLabels, fulfillment.trackingStatus)} />
                </div>

                {fulfillment.trackingUrl ? (
                  <div className="mt-3">
                    <a
                      href={fulfillment.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary btn-sm inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700"
                    >
                      <span>📦</span>
                      {fulfillment.carrierName ?? 'Kolay Gelsin'} ile Takip Et ↗
                    </a>
                  </div>
                ) : null}

                {fulfillment.trackingEvents.length > 0 ? (
                  <ol className="mt-3 space-y-2 border-l border-ink-200 pl-4">
                    {fulfillment.trackingEvents.map((event) => (
                      <li key={`${event.occurredAt}-${event.status}`} className="relative text-sm">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-ink-300" />
                        <p className="text-ink-700">{event.description ?? event.status}</p>
                        <p className="text-xs text-ink-400">
                          {formatDateTime(event.occurredAt)}
                          {event.location ? ` · ${event.location}` : ''}
                        </p>
                      </li>
                    ))}
                  </ol>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Zaman cizelgesi */}
      {data.timeline.length > 0 ? (
        <section className="card p-5">
          <h3 className="mb-4 text-sm font-semibold text-ink-900">Sipariş geçmişi</h3>
          <ol className="space-y-3 border-l border-ink-200 pl-4">
            {data.timeline.map((event) => (
              <li key={event.id} className="relative text-sm">
                <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-brand-500" />
                <p className="text-ink-700">{event.message}</p>
                <p className="text-xs text-ink-400">{formatDateTime(event.createdAt)}</p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <ConfirmDialog
        open={cancelOpen}
        title="Siparişi iptal et"
        message="Bu siparişi iptal etmek istediğinize emin misiniz? İşlem geri alınamaz."
        confirmLabel="Siparişi iptal et"
        danger
        busy={cancel.isPending}
        onCancel={() => setCancelOpen(false)}
        onConfirm={() => cancel.mutate('Müşteri talebi')}
      />

      <ReturnRequestModal
        open={returnOpen}
        onClose={() => setReturnOpen(false)}
        orderId={data.id}
        lines={data.lines.map((line) => ({
          id: line.id,
          title: line.productTitle,
          variantTitle: line.variantTitle ?? null,
          imageUrl: line.imageUrl ?? null,
          returnable: line.quantity - line.refundedQuantity - line.cancelledQuantity,
        }))}
      />
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-600">{label}</dt>
      <dd className="text-ink-900">{value}</dd>
    </div>
  );
}

/* ----------------------------- Iade talebi ----------------------------- */

interface ReturnableLine {
  id: number;
  title: string;
  variantTitle: string | null;
  imageUrl: string | null;
  returnable: number;
}

function ReturnRequestModal({
  open,
  onClose,
  orderId,
  lines,
}: {
  open: boolean;
  onClose: () => void;
  orderId: number;
  lines: ReturnableLine[];
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [reason, setReason] = useState<ReturnReason>('ChangedMind');
  const [note, setNote] = useState('');

  const selected = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([lineId, quantity]) => ({ orderLineId: Number(lineId), quantity }));

  const mutation = useMutation({
    mutationFn: () => {
      const dto: CreateReturnRequestDto = {
        orderId,
        lines: selected,
        reason,
        customerNote: note || null,
        imageUrls: [],
        isExchange: false,
      };
      return accountApi.createReturn(dto);
    },
    onSuccess: (result) => {
      toast.success(`İade talebiniz oluşturuldu: ${result.returnNumber}`);
      void queryClient.invalidateQueries({ queryKey: ['account'] });
      onClose();
      navigate('/hesabim/iadeler');
    },
  });

  return (
    <Modal
      open={open}
      title="İade talebi oluştur"
      description="İade etmek istediğiniz ürünleri ve adetlerini seçin."
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={selected.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
            Talebi gönder
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={mutation.error} />

        <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
          {lines.map((line) => (
            <li key={line.id} className="flex items-center gap-3 p-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-ink-50">
                {line.imageUrl ? (
                  <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-900">{line.title}</p>
                {line.variantTitle ? (
                  <p className="truncate text-xs text-ink-500">{line.variantTitle}</p>
                ) : null}
                <p className="text-xs text-ink-400">İade edilebilir: {line.returnable} adet</p>
              </div>
              <input
                type="number"
                min={0}
                max={line.returnable}
                disabled={line.returnable <= 0}
                value={quantities[line.id] ?? 0}
                onChange={(event) =>
                  setQuantities((current) => ({
                    ...current,
                    [line.id]: Math.max(0, Math.min(Number(event.target.value) || 0, line.returnable)),
                  }))
                }
                className="field w-20 py-1.5 text-center text-sm"
                aria-label={`${line.title} iade adedi`}
              />
            </li>
          ))}
        </ul>

        <Field label="İade nedeni" htmlFor="return-reason" required>
          <select
            id="return-reason"
            className="field"
            value={reason}
            onChange={(event) => setReason(event.target.value as ReturnReason)}
          >
            {returnReasonValues.map((value) => (
              <option key={value} value={value}>
                {returnReasonLabels[value]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Açıklama" htmlFor="return-note">
          <textarea
            id="return-note"
            className="field min-h-20"
            maxLength={1000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ------------------------------ Iadelerim ------------------------------ */

export function MyReturnsPage() {
  const toast = useToast();
  const returns = useQuery({
    queryKey: ['account', 'returns'],
    queryFn: () => accountApi.myReturns(),
  });

  if (returns.isLoading) return <PageLoader />;
  if (returns.isError) return <ErrorState error={returns.error} onRetry={() => void returns.refetch()} />;

  const data = returns.data!;

  if (data.length === 0) {
    return (
      <EmptyState
        title="İade talebiniz yok"
        description="Siparişlerinizden birini görüntüleyip iade talebi oluşturabilirsiniz."
        action={
          <Link to="/hesabim/siparisler" className="btn-secondary">
            Siparişlerime git
          </Link>
        }
      />
    );
  }

  return (
    <ul className="space-y-4">
      {data.map((request) => (
        <li key={request.id} className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-ink-900">{request.returnNumber}</p>
              <p className="mt-0.5 text-sm text-ink-500">
                Sipariş {request.orderNumber} · {formatDate(request.requestedAt)}
              </p>
            </div>
            <Badge label={labelOf(returnStatusLabels, request.status)} />
          </div>

          <p className="mt-3 text-sm text-ink-600">
            Neden: {returnReasonLabels[request.reason] ?? request.reason}
          </p>

          {request.customerNote ? (
            <p className="mt-1 text-sm text-ink-500">{request.customerNote}</p>
          ) : null}

          <ul className="mt-3 space-y-2">
            {request.lines.map((line) => (
              <li key={line.id} className="flex items-center gap-3 text-sm">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50">
                  {line.imageUrl ? (
                    <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <span className="min-w-0 flex-1 truncate text-ink-700">
                  {line.productTitle}
                  {line.variantTitle ? ` · ${line.variantTitle}` : ''}
                </span>
                <span className="text-ink-500">{line.quantityRequested} adet</span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-3 text-sm">
            <span className="text-ink-600">
              Talep edilen tutar: <strong className="text-ink-900">{money(request.requestedAmount)}</strong>
            </span>
          </div>

          {request.returnTrackingNumber ? (
            <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-100 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs text-white">
                    📦
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-brand-900">
                      {request.returnCarrierName ?? 'Kolay Gelsin'} İade Kodu
                    </p>
                    <p className="text-[11px] text-brand-700">Ücretsiz İade Anlaşması</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded bg-white px-2.5 py-1 font-mono text-sm font-bold text-brand-700 shadow-sm border border-brand-200 select-all">
                    {request.returnTrackingNumber}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (navigator.clipboard) {
                        void navigator.clipboard.writeText(request.returnTrackingNumber!);
                        toast.success('İade takip kodu panoya kopyalandı.');
                      }
                    }}
                    className="btn-secondary btn-sm text-xs py-1 px-2.5"
                  >
                    Kopyala
                  </button>
                </div>
              </div>

              <div className="mt-2.5 text-xs text-brand-900 space-y-1">
                <p className="font-medium">Nasıl İade Edeceksiniz?</p>
                <ol className="list-decimal list-inside space-y-0.5 text-brand-800">
                  <li>Ürünü faturası ile birlikte korunaklı bir pakete koyunuz.</li>
                  <li>Kolay Gelsin kuryesine veya en yakın teslimat noktasına gidiniz.</li>
                  <li>Görevliye yukarıdaki <strong>iade kodunu</strong> iletiniz. Kargo ücreti mağazamızca karşılanacaktır.</li>
                </ol>
              </div>

              {request.returnLabelUrl ? (
                <div className="mt-3 pt-2 border-t border-brand-100 flex justify-end">
                  <a
                    href={request.returnLabelUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-brand-700 hover:text-brand-900 inline-flex items-center gap-1"
                  >
                    Kargo Durumunu Takip Et ↗
                  </a>
                </div>
              ) : null}
            </div>
          ) : null}

          {request.rejectionReason ? (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Reddedilme nedeni: {request.rejectionReason}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
